/**
 * The "Remind me" quick presets — later today / tomorrow morning / next week.
 *
 * Each preset is a WALL CLOCK in the viewer's configured timezone ("tomorrow at 9am"
 * means 9am where they are), not an offset from now, so the arithmetic runs on calendar
 * parts resolved through `zonedParts` and is converted back through `wallClockToIso` —
 * which is `datetimeLocal.ts`'s parts-addressed door onto the same DST-correct conversion
 * `LocalizedDateTimeInput` uses. Doing it with the `Date` getters would answer for the
 * BROWSER's ambient zone, a different zone from the user's configured one whenever they
 * travel or set it explicitly, and would drift an hour across every DST boundary.
 *
 * `presetInstant` returns `null` for a preset that has already passed — "later today" at
 * 6pm is not a reminder, it is a rejected create — so the caller can drop the option
 * rather than offer a button the API will refuse.
 */
import { wallClockToIso } from '../utils/datetimeLocal';
import { zonedParts } from '../utils/timezone';

export type ReminderPresetId =
  | 'later_today'
  | 'tomorrow'
  | 'next_week'
  | 'next_month';

/** The end-of-workday hour "later today" targets, in the viewer's timezone. */
const LATER_TODAY_HOUR = 17;
/** The hour both forward-looking presets target. */
const MORNING_HOUR = 9;

/**
 * The same calendar day one month on, clamped to that month's last day.
 *
 * `Date.UTC(2026, 1, 31)` normalizes February 31 forward into March, which would make
 * "next month" from January 31 mean March 3 — so the day is clamped instead, the way
 * every calendar app resolves a month step. Pure calendar arithmetic, no zone in play.
 */
function shiftMonth(year: number, month: number, day: number) {
  const targetYear = month === 12 ? year + 1 : year;
  const targetMonth = month === 12 ? 1 : month + 1;
  // Day 0 of the month AFTER the target is the target's last day.
  const lastDay = new Date(Date.UTC(targetYear, targetMonth, 0)).getUTCDate();
  return { year: targetYear, month: targetMonth, day: Math.min(day, lastDay) };
}

/**
 * The calendar day `delta` days after `{year, month, day}`, as `YYYY-MM-DD`.
 *
 * `Date.UTC` normalizes an out-of-range day (June 31 → July 1), so this is pure calendar
 * arithmetic with no zone in play — the parts went in as a calendar date and come back as
 * one. Day RESOLUTION is what makes it DST-safe: a 23- or 25-hour day is still one day.
 */
function shiftDay(year: number, month: number, day: number, delta: number) {
  const shifted = new Date(Date.UTC(year, month - 1, day + delta));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    /** 0 = Sunday … 6 = Saturday. */
    weekday: shifted.getUTCDay(),
  };
}

/**
 * The UTC ISO instant a preset denotes, or `null` when it is not in the future.
 *
 * @param now  the instant to resolve relative to (injected so this is testable)
 * @param timeZone  the viewer's configured IANA zone
 */
export function presetInstant(
  preset: ReminderPresetId,
  now: Date,
  timeZone: string,
): string | null {
  const today = zonedParts(now, timeZone);

  let target: { year: number; month: number; day: number };
  let hour: number;

  switch (preset) {
    case 'later_today':
      // The calendar DAY only. `zonedParts` also returns the current hour, minute and
      // second, and spreading the whole thing here made "later today" mean 17:<the
      // minute you clicked at> — a bug invisible to any test whose clock reads :00.
      target = { year: today.year, month: today.month, day: today.day };
      hour = LATER_TODAY_HOUR;
      break;
    case 'tomorrow':
      target = shiftDay(today.year, today.month, today.day, 1);
      hour = MORNING_HOUR;
      break;
    case 'next_month':
      target = shiftMonth(today.year, today.month, today.day);
      hour = MORNING_HOUR;
      break;
    case 'next_week': {
      // The next MONDAY, never today: on a Monday this means the Monday a week out,
      // which is what "next week" reads as on any day of the week.
      const weekday = shiftDay(today.year, today.month, today.day, 0).weekday;
      const delta = (8 - weekday) % 7 || 7;
      target = shiftDay(today.year, today.month, today.day, delta);
      hour = MORNING_HOUR;
      break;
    }
  }

  // `minute` is spelled explicitly rather than left to the helper's default, so a target
  // that ever carries one cannot silently override the hour-on-the-hour contract.
  const iso = wallClockToIso({ ...target, hour, minute: 0 }, timeZone);
  if (!iso) return null;
  return new Date(iso).getTime() > now.getTime() ? iso : null;
}

/** Presets in render order, paired with the i18n leaf under `common:reminders`. */
export const REMINDER_PRESETS: ReadonlyArray<{ id: ReminderPresetId; labelKey: string }> = [
  { id: 'later_today', labelKey: 'common:reminders.laterToday' },
  { id: 'tomorrow', labelKey: 'common:reminders.tomorrow' },
  { id: 'next_week', labelKey: 'common:reminders.nextWeek' },
];

/**
 * The presets offered when SILENCING a nag, which is a different question.
 *
 * "Remind me" asks when to be pinged about something you chose; snooze asks how long
 * to be left alone about something the system raised — so it starts a day out rather
 * than at 5pm today (a nag that returns this afternoon has not been snoozed) and it
 * reaches a month, which is the span a quiet relationship is measured in.
 */
export const SNOOZE_PRESETS: ReadonlyArray<{ id: ReminderPresetId; labelKey: string }> = [
  { id: 'tomorrow', labelKey: 'common:reminders.tomorrow' },
  { id: 'next_week', labelKey: 'common:reminders.nextWeek' },
  { id: 'next_month', labelKey: 'common:reminders.nextMonth' },
];
